import React, { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import LoadingButton from "../../../components/LoadingButton";
import makeStyles from "@mui/styles/makeStyles";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import { Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText, } from "@mui/material";
import { callAPI } from "../../../lib/common";
import BoxActions from "./BoxActions";
import { usePortal } from "../../../Providers/PortalProvider";
import Level2BoxContainer from "./Level2BoxContainer";
import FormikTextField from "../../../components/Common/FormikTextField";
import { Level2BoxHeading } from "./Level2BoxHeading";
import useIsMobileDevice from "../../../hooks/useIsMobileDevice";
import { useAuth } from "../../providers/AuthProvider";
import { getYUPValidators } from "../../../components/yupvalidators";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import InputMobileNumber from "../../Desktop/Pages/SubjectRegistration/Components/InputMobileNumber";
const useStyles = makeStyles((theme) => ({
    form: {
        marginBottom: 0,
    },
    container: {
        marginBottom: theme.spacing(3),
    },
    termsOfServiceText: {
        textAlign: "center",
        fontWeight: 400,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        "& > a": Object.assign(Object.assign({}, theme.typography.button), { padding: 0, minWidth: 0, display: "inline" }),
    },
    acceptMail: {
        marginTop: theme.spacing(2),
        "& .MuiTypography-body1": {
            fontSize: "12px",
        },
    },
}));
const Register = ({ email, onLoggedIn, subjectInfo, handleGoBack, }) => {
    const [error, setError] = useState("");
    const { t } = useTranslation();
    const classes = useStyles();
    const [termsOfServiceOpen, setTermsOfServiceOpen] = useState(false);
    const isMobile = useIsMobileDevice();
    const { isAuthenticated, loadCurrentUser, user } = useAuth();
    const portal = usePortal();
    const didHandleLoginSuccess = useRef(false);
    useEffect(() => {
        if (!isAuthenticated || didHandleLoginSuccess.current)
            return;
        didHandleLoginSuccess.current = true;
        if (user && window.isEpmsAnalyticsFeatureEnabled && window.epmsAnalytics) {
            window.epmsAnalytics.onRegisterUser(user);
        }
        onLoggedIn();
    }, [isAuthenticated, onLoggedIn, user]);
    const openTermsOfService = (e) => {
        e.preventDefault();
        setTermsOfServiceOpen(true);
    };
    const initialValues = {
        mobile: "",
        password: "",
        acceptmail: portal.options.disableAcceptMailOptionWhenRegisteringUser
            ? null
            : portal.options.acceptMailDefaultOn
                ? true
                : false,
    };
    const acceptMailLabel = portal.options.acceptMailDefaultOn
        ? t("Yes, I agree to receive relevant offers by email. This applies only to the images I have been given secure access to from %s", {
            postProcess: "sprintf",
            sprintf: [portal.name],
        })
        : t("Yes, I agree to receive promotional e-mails and SMS from %s", {
            postProcess: "sprintf",
            sprintf: [portal.name],
        });
    return (React.createElement(Level2BoxContainer, { className: classes.container },
        React.createElement(Level2BoxHeading, { color: "primary" },
            React.createElement(Trans, null, "Create a free user account")),
        React.createElement(Formik, { initialValues: initialValues, validationSchema: SignupSchema(t), onSubmit: (values, { setSubmitting, setErrors }) => {
                setSubmitting(true);
                callAPI("/ajax/create.php", "POST", Object.assign(Object.assign({}, values), { email, code: subjectInfo.code, acceptmail: values.acceptmail, confirm_password: values.password, termsofservice: true }))
                    .then((res) => {
                    if (res.error) {
                        setError(res.error);
                        setErrors(res.data.reduce((errors, error) => {
                            errors[error] = true;
                            return errors;
                        }, {}));
                    }
                    else {
                        loadCurrentUser();
                    }
                })
                    .finally(() => {
                    setSubmitting(false);
                });
            } }, ({ submitForm, isSubmitting, isValid, values, handleChange }) => {
            return (React.createElement(Form, { className: classes.form },
                React.createElement(Field, { id: "register-email-address", component: FormikTextField, label: t("Email address"), disabled: true, name: "email", variant: "outlined", fullWidth: true, value: email, icon: "mail_outline" }),
                React.createElement(Field, { id: "register-mobile-phone", component: InputMobileNumber, name: "mobile", label: t("Phone number"), margin: "normal", fullWidth: true }),
                React.createElement(Field, { id: "register-password", component: FormikTextField, type: "password", label: t("Choose a password"), name: "password", variant: "outlined", fullWidth: true, icon: "lock_outline" }),
                !portal.options.disableAcceptMailOptionWhenRegisteringUser && (React.createElement(FormControlLabel, { id: "register-accept-mail", control: React.createElement(Checkbox, { name: "acceptmail", checked: Boolean(values.acceptmail), onChange: handleChange }), label: acceptMailLabel, className: classes.acceptMail })),
                React.createElement(FormHelperText, { error: !!error }, error),
                React.createElement(Box, { mt: 1, mb: 1 },
                    React.createElement(Typography, { variant: "body1", className: classes.termsOfServiceText },
                        React.createElement(Trans, null,
                            "By signing up, I agree to the",
                            " ",
                            React.createElement(Link, { onClick: openTermsOfService, color: "primary" }, "Terms of service")))),
                React.createElement(BoxActions, null,
                    !isMobile && (React.createElement(Button, { variant: "outlined", color: "primary", onClick: handleGoBack },
                        React.createElement(Trans, null, "Go back"))),
                    React.createElement(LoadingButton, { variant: "primary", isLoading: isSubmitting, onClick: submitForm, disabled: !isValid, fullWidth: true, "data-testid": "create-user-button" },
                        React.createElement(Trans, null, "Create user")))));
        }),
        termsOfServiceOpen && (React.createElement(TermsOfServiceDialog, { onClose: () => setTermsOfServiceOpen(false) }))));
};
const TermsOfServiceDialog = ({ onClose }) => {
    const { terms } = usePortal();
    return (React.createElement(Dialog, { open: true },
        React.createElement(DialogTitle, null,
            React.createElement(Trans, null, "Terms of service")),
        React.createElement(DialogContent, null,
            React.createElement("div", { dangerouslySetInnerHTML: {
                    __html: terms.termsOfService.replace(/(?:\r\n|\r|\n)/g, "<br/>"),
                } })),
        React.createElement(DialogActions, null,
            React.createElement(Button, { onClick: onClose, color: "primary" },
                React.createElement(Trans, null, "Ok")))));
};
const SignupSchema = (t) => () => {
    const validators = getYUPValidators(t);
    return Yup.object().shape({
        mobile: validators.MOBILE_PHONE_WITH_SERVER_VALIDATION,
        password: Yup.string()
            .min(6, t("That's too short!"))
            .required(t("This is required!")),
    });
};
export default Register;
